import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/report-page.css";

const ReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get message data from navigation state
  const { message, decodedContent, apiUrl } = location.state || {};
  
  const [decodingKey, setDecodingKey] = useState('');
  const [localDecodedContent, setLocalDecodedContent] = useState(null);
  const [successfulDecodingKey, setSuccessfulDecodingKey] = useState(''); // Track the key that worked
  const [reportReason, setReportReason] = useState('spam');
  const [reportDetails, setReportDetails] = useState('');
  const [isDecoding, setIsDecoding] = useState(false);
  const [decodeError, setDecodeError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Report reasons
  const reportReasons = [
    { value: 'spam', label: 'Spam' },
    { value: 'abuse', label: 'Abuse/Harassment' },
    { value: 'inappropriate', label: 'Inappropriate Content' },
    { value: 'violence', label: 'Violence/Threats' },
    { value: 'hate_speech', label: 'Hate Speech' },
    { value: 'illegal', label: 'Illegal Content' },
    { value: 'scam', label: 'Scam/Fraud' },
    { value: 'copyright', label: 'Copyright Violation' },
    { value: 'other', label: 'Other' }
  ];

  // Redirect if no message data
  useEffect(() => {
    if (!message) {
      navigate('/');
      return;
    }
  }, [message, navigate]);

  if (!message) {
    return <div>Loading...</div>;
  }

  // Calculate expiration info
  const now = Math.floor(Date.now() / 1000);
  const expiresIn = message.ttl - now;
  const expiryText = expiresIn > 0 
    ? `Expires in ${Math.floor(expiresIn / 60)} ${Math.floor(expiresIn / 60) === 1 ? 'min' : 'mins'}`
    : 'Expired';

  // Check if message is already decoded
  const isAlreadyDecoded = decodedContent !== null;
  const hasDecodedContent = isAlreadyDecoded || localDecodedContent !== null;

  const decodeMessage = async () => {
    if (!decodingKey.trim()) {
      setDecodeError('Please enter the access code');
      return;
    }

    setIsDecoding(true);
    setDecodeError('');

    try {
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(`${apiUrl}/messages/${message.id}?encryption_key=${decodingKey}`, {
        method: 'GET',
        headers: {
          'Authorization': authToken
        }
      });

      const data = await response.json();

      if (response.ok && data.message) {
        // Store the successful decoding key
        setSuccessfulDecodingKey(decodingKey);
        
        if (data.message.content_type === 'text' && data.message.decoded_text) {
          setLocalDecodedContent({
            type: 'text',
            content: data.message.decoded_text
          });
        } else if (data.message.content_type === 'audio' && data.message.decoded_audio_base64) {
          setLocalDecodedContent({
            type: 'audio',
            content: `data:audio/mp3;base64,${data.message.decoded_audio_base64}`
          });
        } else {
          setDecodeError('Could not decode message content');
        }
      } else {
        if (response.status === 401) {
          setDecodeError('Please login to decode messages');
        } else {
          setDecodeError(data.error || 'Invalid access code');
        }
      }
    } catch (error) {
      console.error('Error decoding message:', error);
      setDecodeError('Failed to connect to the server. Please try again later.');
    } finally {
      setIsDecoding(false);
    }
  };

  const handleSubmitReport = async () => {
    // Validate that message is decoded
    if (!hasDecodedContent) {
      alert('Please decode the message before submitting your report.');
      return;
    }

    // Validate that we have an access code to send to support team
    if (!successfulDecodingKey) {
      alert('Please provide the access code so our support team can review the message content.');
      return;
    }

    setIsSubmitting(true);

    try {
      const authToken = localStorage.getItem('authToken');
      const sessionId = localStorage.getItem('tempUserId');

      const reportData = {
        type: 'report',
        message_id: message.id,
        user_session_id: sessionId,
        reason: reportReason,
        additional_details: reportDetails.trim(),
        timestamp: Math.floor(Date.now() / 1000),
        coordinates: `${message.x_coordinate},${message.y_coordinate}`,
        // Include the access code for support team to decode the message
        access_code: successfulDecodingKey,
        // Include metadata about the content type
        content_type: message.content_type,
        user_decoded_content_type: localDecodedContent?.type || decodedContent?.type || message.content_type
      };

      const response = await fetch(`${apiUrl}/messages/${message.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        },
        body: JSON.stringify(reportData)
      });

      if (response.ok) {
        alert('Message reported successfully. Our team will review it within 24 hours.');
        navigate('/'); // Navigate back to main page
      } else {
        const data = await response.json();
        alert(`Error reporting message: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <div className="report-page">
      <div className="report-header">
        <button className="back-button" onClick={handleBack}>
          ← Back
        </button>
        <h2>🚩 Report Message</h2>
      </div>

      <div className="report-content">
        {/* Message Information */}
        <div className="message-info-card">
          <h3>Message Details</h3>
          <div className="message-details">
            <div className="detail-row">
              <span className="label">Location:</span>
              <span className="value">({message.x_coordinate}, {message.y_coordinate})</span>
            </div>
            <div className="detail-row">
              <span className="label">Type:</span>
              <span className="value">{message.content_type === 'text' ? 'Text Message' : 'Audio Message'}</span>
            </div>
            <div className="detail-row">
              <span className="label">Status:</span>
              <span className={`value ${expiresIn <= 0 ? 'expired' : 'active'}`}>{expiryText}</span>
            </div>
            {hasDecodedContent && (
              <div className="detail-row">
                <span className="label">Content:</span>
                <span className="value decoded">✅ Decoded (helps our moderation team)</span>
              </div>
            )}
          </div>
        </div>

        {/* Message Decoding Section */}
        {!isAlreadyDecoded && (
          <div className="decode-section-card required">
            <h3>Decode Message (Required) <span className="required-badge">*</span></h3>
            <p className="decode-help">
              You must decode the message using the access code to see what you're reporting. 
              The access code will be included with your report so our support team can also decode and review the message content.
            </p>
            
            <div className="decode-input-group">
              <input
                type="text"
                placeholder="Enter access code"
                value={decodingKey}
                onChange={(e) => setDecodingKey(e.target.value)}
                className="decode-input"
                onKeyPress={(e) => e.key === 'Enter' && decodeMessage()}
              />
              <button 
                onClick={decodeMessage}
                disabled={isDecoding || !decodingKey.trim()}
                className="decode-button"
              >
                {isDecoding ? 'Decoding...' : 'Decode'}
              </button>
            </div>

            {decodeError && (
              <div className="decode-error">{decodeError}</div>
            )}

            {localDecodedContent && (
              <div className="decoded-content">
                <h4>Decoded Content:</h4>
                {localDecodedContent.type === 'text' ? (
                  <div className="decoded-text">
                    <div className="content-preview">
                      {localDecodedContent.content}
                    </div>
                  </div>
                ) : (
                  <div className="decoded-audio">
                    <audio controls src={localDecodedContent.content}></audio>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Existing Decoded Content Display */}
        {isAlreadyDecoded && (
          <div className="existing-decoded-card">
            <h3>✅ Previously Decoded Content</h3>
            <p>You have already decoded this message in a previous session.</p>
            {decodedContent && (
              <div className="existing-content-preview">
                <div className="preview-label">What you previously decoded:</div>
                {decodedContent.type === 'text' ? (
                  <div className="content-text">{decodedContent.content}</div>
                ) : (
                  <div>{message.content_type === 'text' ? 'Text Message' : 'Audio Message'} content that you previously decoded</div>
                )}
              </div>
            )}
            
            <div className="re-decode-notice">
              <h4>🔑 Access Code Required for Report</h4>
              <p>To submit a report, please re-enter the access code. Our support team needs this code to review the message content.</p>
              
              <div className="decode-input-group">
                <input
                  type="text"
                  placeholder="Re-enter access code for report"
                  value={decodingKey}
                  onChange={(e) => setDecodingKey(e.target.value)}
                  className="decode-input"
                />
                <button 
                  onClick={() => setSuccessfulDecodingKey(decodingKey)}
                  disabled={!decodingKey.trim()}
                  className="confirm-key-button"
                >
                  Confirm Access Code
                </button>
              </div>
              
              {successfulDecodingKey && (
                <div className="key-confirmed">
                  ✅ Access code confirmed - you can now submit your report
                </div>
              )}
            </div>
          </div>
        )}

        {/* Decode Requirement Notice */}
        {!hasDecodedContent && (
          <div className="decode-requirement-notice">
            <h4>🔒 Content Access Required</h4>
            <p>To maintain reporting quality and prevent false reports, you must decode the message content before submitting a report. This ensures our moderation team can:</p>
            <ul>
              <li>Review the actual content that violates policies</li>
              <li>Make accurate moderation decisions</li>
              <li>Distinguish between legitimate reports and false claims</li>
              <li>Provide appropriate responses to policy violations</li>
            </ul>
          </div>
        )}

        {/* Report Form */}
        <div className="report-form-card">
          <h3>Report Details</h3>
          
          <div className="form-group">
            <label htmlFor="report-reason">Reason for reporting:</label>
            <select 
              id="report-reason"
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              className="report-select"
            >
              {reportReasons.map(reason => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="additional-details">Additional Details (Optional):</label>
            <textarea
              id="additional-details"
              placeholder="Please provide any additional context that would help our moderation team..."
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
              className="details-textarea"
              rows="4"
              maxLength="500"
            />
            <div className="char-counter">{reportDetails.length}/500</div>
          </div>

          <div className="privacy-notice">
            <h4>🔒 Privacy & Review Process</h4>
            <ul>
              <li>Your report will be reviewed by our moderation team within <strong>24 hours</strong></li>
              <li>The <strong>access code</strong> will be shared with our support team so they can decode and review the message content</li>
              <li>Only your session ID (not your login credentials) will be included with the report</li>
              <li>Reports are used solely for content moderation purposes</li>
              <li>False reports or reports without proper context may result in account restrictions</li>
              <li>All reports and access codes are handled confidentially by our moderation team</li>
            </ul>
          </div>

          <div className="report-actions">
            <button 
              onClick={handleBack}
              className="cancel-report-button"
            >
              Cancel
            </button>
            <button 
              onClick={handleSubmitReport}
              disabled={isSubmitting || !hasDecodedContent || !successfulDecodingKey}
              className="submit-report-button"
            >
              {isSubmitting ? (
                <>
                  <span className="loading-spinner">⏳</span>
                  Submitting Report...
                </>
              ) : !hasDecodedContent ? (
                '🔒 Decode Message to Submit Report'
              ) : !successfulDecodingKey ? (
                '🔑 Confirm Access Code to Submit Report'
              ) : (
                '🚩 Submit Report'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPage;